import React, { useState } from 'react';
import { ColumnType } from 'antd/lib/table';
import { Key } from 'ts-key-enum';

import './index.less';

import IbTable from '../../../../../components/common/IbTable';
import IbTypography from '../../../../../components/common/IbTypography';
import { getTranslateFunc } from '../../../../../utils/stringUtil';
import { ELMA365ToolParameterOutput, ELMA365ToolPropertyEntryStr } from '../../../../../../gamma-api';
import IbSwitch from '../../../../../components/common/IbSwitch';
import IbCheckbox from '../../../../../components/common/IbCheckbox';
import { getFormPropertyType } from '../../../../projects/ProjectPage/sources/SourceSettings/ELMA365AppFieldsSelector/utils';
import IbTextArea from '../../../../../components/common/IbTextArea';
import IbButton from '../../../../../components/common/IbButton';
import IbIcon from '../../../../../components/common/IbIcon';

import {
  MAIN_CLASS_NAME,
  TABLE_ROW_CLASS_NAME,
  CELL_SWITCH_CLASS_NAME,
  TABLE_ROW_DISABLED_CLASS_NAME,
  CELL_DESCRIPTION_CLASS_NAME,
} from './const';

export interface IParamsTableProps {
  parameters: ELMA365ToolParameterOutput[];
  loading?: boolean;
  onParameterChange: (parameter: ELMA365ToolParameterOutput) => void;
}

const ParamsTable: React.FC<IParamsTableProps> = ({ parameters, loading, onParameterChange }) => {
  const translate = getTranslateFunc('Toolkits: ELMA365: Tool: Params table:');

  const [editingDescriptionParam, setEditingDescriptionParam] = useState<ELMA365ToolParameterOutput>();
  const [editingDescription, setEditingDescription] = useState('');

  const renderCell = (text: React.ReactNode) => {
    return (
      <IbTypography.Paragraph ellipsis type="secondary">
        {text}
      </IbTypography.Paragraph>
    );
  };

  const renderCodeCell = (parameter: ELMA365ToolParameterOutput) => {
    const onSwitchChange = (value: boolean) => {
      parameter.enabled.value = value;
      onParameterChange(parameter);
    };

    return (
      <div className={CELL_SWITCH_CLASS_NAME}>
        <IbSwitch checked={parameter.enabled.value} size="small" onChange={onSwitchChange} />
        <IbTypography.Paragraph ellipsis type="secondary">
          {parameter.code}
        </IbTypography.Paragraph>
      </div>
    );
  };

  const renderDescriptionCell = (parameter: ELMA365ToolParameterOutput) => {
    if (editingDescriptionParam?.code === parameter.code) {
      const onInputBlur = () => {
        parameter.description.value = editingDescription;
        onParameterChange(parameter);
        setEditingDescriptionParam(undefined);
      };

      const onInputChange = (value: string) => {
        setEditingDescription(value);
      };

      const onInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === Key.Escape) {
          e.stopPropagation();
          setEditingDescriptionParam(undefined);
          setEditingDescription('');
          return;
        }

        if (e.key === Key.Enter && !e.shiftKey) {
          e.preventDefault();
          onInputBlur();
        }
      };

      return (
        <IbTextArea
          autoFocus
          value={editingDescription}
          onBlur={onInputBlur}
          onChange={onInputChange}
          onKeyDown={onInputKeyDown}
        />
      );
    }

    const editParameter = () => {
      setEditingDescription(parameter.description.value);
      setEditingDescriptionParam(parameter);
    };

    return (
      <span className={CELL_DESCRIPTION_CLASS_NAME} onDoubleClick={editParameter}>
        <IbTypography.Paragraph ellipsis type="secondary">
          {parameter.description.value}
        </IbTypography.Paragraph>
        <IbButton icon={<IbIcon iconName="edit" size={16} />} type="icon" onClick={editParameter} />
      </span>
    );
  };

  const renderRequiredCell = (parameter: ELMA365ToolParameterOutput) => {
    const onCheckboxChange = (value: boolean) => {
      parameter.required.value = value;
      onParameterChange(parameter);
    };

    return (
      <IbCheckbox disabled={!parameter.enabled.value} value={parameter.required.value} onChange={onCheckboxChange} />
    );
  };

  const columns: ColumnType<ELMA365ToolParameterOutput>[] = [
    {
      title: translate('Code'),
      render: renderCodeCell,
      width: '15%',
    },
    {
      title: translate('Description'),
      render: renderDescriptionCell,
      ellipsis: true,
    },
    {
      title: translate('Name'),
      dataIndex: 'name',
      render: (entry: ELMA365ToolPropertyEntryStr) => renderCell(entry.value),
      width: '20%',
    },

    {
      title: translate('ELMA365 Name'),
      dataIndex: 'title',
      render: (entry: ELMA365ToolPropertyEntryStr) => renderCell(entry.value),
      width: '15%',
    },
    {
      title: translate('ELMA365 Type'),
      dataIndex: 'type',
      render: (text: string) => renderCell(getFormPropertyType(text.toLowerCase())),
      width: '15%',
    },
    {
      title: translate('Required'),
      render: renderRequiredCell,
      width: '10%',
    },
  ];

  const rowClassNamesFunc = (parameter: ELMA365ToolParameterOutput) => {
    const rowClassNames = [TABLE_ROW_CLASS_NAME];
    if (!parameter.enabled.value) {
      rowClassNames.push(TABLE_ROW_DISABLED_CLASS_NAME);
    }

    return rowClassNames;
  };

  const emptyText = translate('Empty content: Nothing found');

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbTable
        columns={columns}
        dataSource={parameters}
        emptyText={emptyText}
        loading={loading}
        rowClassNamesFunc={rowClassNamesFunc}
        rowKey="code"
      />
    </div>
  );
};

export default ParamsTable;
